"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiOutlinePhotograph } from "react-icons/hi";
import { LuLoader2 } from "react-icons/lu";
import { RiSearchLine } from "react-icons/ri";
import { AnnotationOverlay } from "@/app/[tenant]/(console)/scribe/playground/[id]/annotation-overlay";
import { AttachmentPreviewImage } from "@/components/scribe/attachment-preview-image";
import { Button, Checkbox, Input } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDebounce } from "@/hooks/use-debounce";
import { cx } from "@/lib/tailwind-utils";
import type { Annotation } from "@/types/annotation";
import { AttachmentDayPage, NoteAttachment } from "@/types/notes";
import { useLinkAttachmentsMutation } from "@/api/provider/scribe/note-mutation";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
function flattenPages(pages: (AttachmentDayPage | null)[] | undefined, existingAttachmentIds: Set<string>) {
  const allPages = pages?.filter(Boolean) as AttachmentDayPage[] ?? [];
  return allPages.flatMap(page => page.data.map(attachment => ({
    id: attachment.id,
    signedUrl: attachment.signedUrl,
    thumbnailSignedUrl: attachment.thumbnailSignedUrl,
    name: attachment.name,
    tagNames: attachment.tags?.map(tag => tag.name) ?? [],
    annotations: (attachment.annotations ?? []) as Annotation[],
    date: page.date,
    isLinked: existingAttachmentIds.has(attachment.id)
  })));
}
interface GalleryPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  noteId: string;
  existingAttachmentIds: Set<string>;
}
export default function GalleryPickerModal(t0) {
  const $ = _c(86);
  const {
    open,
    onOpenChange,
    patientId,
    noteId,
    existingAttachmentIds
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selectedIds, setSelectedIds] = useState(t1);
  const [search, setSearch] = useState("");
  let t2;
  if ($[1] !== search) {
    t2 = search.trim().toLowerCase();
    $[1] = search;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const query = t2;
  const debouncedQuery = useDebounce(query, 300);
  let t3;
  let t4;
  if ($[3] !== open) {
    t3 = () => {
      if (open) {
        setSelectedIds(new Set());
      } else {
        setSearch("");
      }
    };
    t4 = [open];
    $[3] = open;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== patientId) {
    t5 = noteQueryOptions.attachmentsInfinite(patientId);
    $[6] = patientId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== open || $[9] !== t5) {
    t6 = {
      ...t5,
      enabled: open,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: _temp
    };
    $[8] = open;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const galleryQuery = useInfiniteQuery(t6);
  let t7;
  if ($[11] !== debouncedQuery || $[12] !== patientId) {
    t7 = noteQueryOptions.attachmentsInfinite(patientId, undefined, undefined, debouncedQuery || undefined);
    $[11] = debouncedQuery;
    $[12] = patientId;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = open && !!debouncedQuery;
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = {
      ...t7,
      enabled: t8,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: _temp2,
      placeholderData: keepPreviousData
    };
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const searchQuery = useInfiniteQuery(t9);
  const isSearching = !!query;
  const searchSettled = query === debouncedQuery && !searchQuery.isPending && !searchQuery.isPlaceholderData;
  const hasNextPage = isSearching ? searchSettled && searchQuery.hasNextPage : galleryQuery.hasNextPage;
  const {
    fetchNextPage,
    isFetchingNextPage
  } = isSearching ? searchQuery : galleryQuery;
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  let t10;
  if ($[17] !== fetchNextPage || $[18] !== hasNextPage || $[19] !== isFetchingNextPage) {
    t10 = node => {
      if (isFetchingNextPage) {
        return;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) {
        observerRef.current.observe(node);
      }
      sentinelRef.current = node;
    };
    $[17] = fetchNextPage;
    $[18] = hasNextPage;
    $[19] = isFetchingNextPage;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const setObserver = t10;
  galleryQuery.data?.pages;
  const t11 = galleryQuery.data?.pages;
  let t12;
  if ($[21] !== existingAttachmentIds || $[22] !== t11) {
    t12 = flattenPages(t11, existingAttachmentIds);
    $[21] = existingAttachmentIds;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const attachments = t12;
  let t13;
  if ($[24] !== existingAttachmentIds || $[25] !== searchQuery.data?.pages) {
    t13 = () => flattenPages(searchQuery.data?.pages, existingAttachmentIds);
    $[24] = existingAttachmentIds;
    $[25] = searchQuery.data?.pages;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  searchQuery.data?.pages;
  let t14;
  if ($[27] !== t13) {
    t14 = t13();
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const searchResults = t14;
  let t15;
  bb0: {
    if (!query) {
      t15 = attachments;
      break bb0;
    }
    let t16;
    if ($[29] !== attachments || $[30] !== query || $[31] !== searchResults) {
      const matches = new Map();
      for (const a of [...attachments, ...searchResults]) {
        if (!matches.has(a.id) && (a.name?.toLowerCase().includes(query) || a.tagNames.some(tag => tag.toLowerCase().includes(query)))) {
          matches.set(a.id, a);
        }
      }
      t16 = Array.from(matches.values());
      $[29] = attachments;
      $[30] = query;
      $[31] = searchResults;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    t15 = t16;
  }
  const visibleAttachments = t15;
  let t16;
  if ($[33] !== visibleAttachments) {
    t16 = visibleAttachments.filter(_temp3);
    $[33] = visibleAttachments;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const selectableAttachments = t16;
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = attachmentId => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(attachmentId)) {
          next.delete(attachmentId);
        } else {
          next.add(attachmentId);
        }
        return next;
      });
    };
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const handleToggleSelection = t17;
  const linkMutation = useLinkAttachmentsMutation(patientId, noteId);
  let t18;
  if ($[36] !== galleryQuery.data?.pages || $[37] !== linkMutation || $[38] !== onOpenChange || $[39] !== searchQuery || $[40] !== selectedIds) {
    t18 = () => {
      if (selectedIds.size === 0) {
        return;
      }
      const byId = new Map();
      for (const page of [...(galleryQuery.data?.pages ?? []), ...(searchQuery.data?.pages ?? [])]) {
        for (const attachment of page?.data ?? []) {
          byId.set(attachment.id, attachment);
        }
      }
      const selected = Array.from(selectedIds).map(id => byId.get(id)).filter(_temp4);
      linkMutation.mutate({
        attachments: selected
      });
      onOpenChange(false);
    };
    $[36] = galleryQuery.data?.pages;
    $[37] = linkMutation;
    $[38] = onOpenChange;
    $[39] = searchQuery;
    $[40] = selectedIds;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const handleAddToNote = t18;
  const selectableCount = selectableAttachments.length;
  let t19;
  if ($[42] !== selectableAttachments || $[43] !== selectableCount || $[44] !== selectedIds) {
    t19 = selectableCount > 0 && selectableAttachments.every(a_2 => selectedIds.has(a_2.id));
    $[42] = selectableAttachments;
    $[43] = selectableCount;
    $[44] = selectedIds;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const allSelected = t19;
  let t20;
  if ($[46] !== allSelected || $[47] !== selectableAttachments) {
    t20 = () => {
      setSelectedIds(prev_0 => {
        const next_0 = new Set(prev_0);
        for (const a_3 of selectableAttachments) {
          if (allSelected) {
            next_0.delete(a_3.id);
          } else {
            next_0.add(a_3.id);
          }
        }
        return next_0;
      });
    };
    $[46] = allSelected;
    $[47] = selectableAttachments;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const handleSelectAll = t20;
  let t21;
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <DialogTitle>Add from Gallery</DialogTitle>;
    t22 = <DialogDescription>Select existing photos to add to this note</DialogDescription>;
    $[49] = t21;
    $[50] = t22;
  } else {
    t21 = $[49];
    t22 = $[50];
  }
  let t23;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <RiSearchLine className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" aria-hidden="true" />;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e => setSearch(e.target.value);
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== search) {
    t25 = <DialogHeader>{t21}{t22}<div className="relative mt-3">{t23}<Input type="search" value={search} onChange={t24} placeholder="Search by photo name or tag" aria-label="Search gallery" className="pl-9" /></div></DialogHeader>;
    $[53] = search;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== allSelected || $[56] !== attachments || $[57] !== galleryQuery.isLoading || $[58] !== handleSelectAll || $[59] !== hasNextPage || $[60] !== isFetchingNextPage || $[61] !== isSearching || $[62] !== search || $[63] !== searchSettled || $[64] !== selectableCount || $[65] !== selectedIds || $[66] !== setObserver || $[67] !== visibleAttachments) {
    t26 = <DialogBody className="max-h-[60vh] overflow-y-auto">{galleryQuery.isLoading && attachments.length === 0 ? <div className="flex items-center justify-center p-8"><LuLoader2 className="h-6 w-6 animate-spin text-foreground-muted" /></div> : attachments.length === 0 ? <div className="flex flex-col items-center justify-center p-8 text-center"><HiOutlinePhotograph className="mb-3 h-12 w-12 text-foreground-muted" /><p className="text-body text-muted-foreground">No photos in this patient's gallery</p><p className="mt-1 text-caption text-foreground-muted">Upload photos first using the "Add Files" button</p></div> : isSearching && searchSettled && !hasNextPage && visibleAttachments.length === 0 ? <div className="flex flex-col items-center justify-center p-8 text-center"><HiOutlinePhotograph className="mb-3 h-12 w-12 text-foreground-muted" /><p className="text-body text-muted-foreground">No photos match "{search.trim()}"</p></div> : <>{selectableCount > 0 && <div className="mb-3 flex items-center gap-2 border-b pb-2"><Checkbox checked={allSelected} onCheckedChange={handleSelectAll} id="select-all" /><label htmlFor="select-all" className="cursor-pointer text-body text-foreground-secondary">Select all ({selectableCount} available)</label></div>}<div className="grid grid-cols-4 gap-3">{visibleAttachments.map(attachment_0 => {
            const isSelected = selectedIds.has(attachment_0.id);
            const isDisabled = attachment_0.isLinked;
            return <div key={attachment_0.id} className={cx("relative cursor-pointer rounded-lg border-2 transition-all", isDisabled ? "cursor-not-allowed border-border opacity-50" : isSelected ? "border-primary ring-2 ring-primary/20" : "border-transparent hover:border-border")} onClick={() => {
              if (!isDisabled) {
                handleToggleSelection(attachment_0.id);
              }
            }}><div className="absolute left-1.5 top-1.5 z-10"><Checkbox checked={isSelected || isDisabled} disabled={isDisabled} className={cx("h-5 w-5 border-2 shadow-sm", isDisabled ? "border-muted bg-muted" : "border-background bg-foreground/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary")} onCheckedChange={() => {
                  if (!isDisabled) {
                    handleToggleSelection(attachment_0.id);
                  }
                }} onClick={_temp5} /></div>{isDisabled && <div className="absolute right-1.5 top-1.5 z-10 rounded bg-background/90 px-1.5 py-0.5 text-caption text-foreground-secondary">Added</div>}<PickerTileImage thumbnailSignedUrl={attachment_0.thumbnailSignedUrl} signedUrl={attachment_0.signedUrl} alt={attachment_0.name || "Attachment"} annotations={attachment_0.annotations} />{attachment_0.name && <p className="mt-1 truncate px-1 text-caption text-foreground-secondary">{attachment_0.name}</p>}</div>;
          })}</div>{(hasNextPage || isSearching && !searchSettled) && <div ref={setObserver} className="py-4 text-center text-caption text-foreground-muted">{isSearching ? <span className="inline-flex items-center gap-2"><LuLoader2 className="h-4 w-4 animate-spin" />Searching older photos</span> : isFetchingNextPage ? <LuLoader2 className="mx-auto h-4 w-4 animate-spin" /> : "Scroll to load more"}</div>}</>}</DialogBody>;
    $[55] = allSelected;
    $[56] = attachments;
    $[57] = galleryQuery.isLoading;
    $[58] = handleSelectAll;
    $[59] = hasNextPage;
    $[60] = isFetchingNextPage;
    $[61] = isSearching;
    $[62] = search;
    $[63] = searchSettled;
    $[64] = selectableCount;
    $[65] = selectedIds;
    $[66] = setObserver;
    $[67] = visibleAttachments;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== onOpenChange) {
    t27 = <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>;
    $[69] = onOpenChange;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  const t28 = selectedIds.size === 0;
  const t29 = `Add to Note${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`;
  let t30;
  if ($[71] !== handleAddToNote || $[72] !== t28 || $[73] !== t29) {
    t30 = <Button onClick={handleAddToNote} disabled={t28}>{t29}</Button>;
    $[71] = handleAddToNote;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t27 || $[76] !== t30) {
    t31 = <DialogFooter>{t27}{t30}</DialogFooter>;
    $[75] = t27;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t25 || $[79] !== t26 || $[80] !== t31) {
    t32 = <DialogContent className="max-w-3xl">{t25}{t26}{t31}</DialogContent>;
    $[78] = t25;
    $[79] = t26;
    $[80] = t31;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== onOpenChange || $[83] !== open || $[84] !== t32) {
    t33 = <Dialog open={open} onOpenChange={onOpenChange}>{t32}</Dialog>;
    $[82] = onOpenChange;
    $[83] = open;
    $[84] = t32;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  return t33;
}

// Draws a chart's marks over its tile, so a charted face chart doesn't look
// like a blank one with the same name.
function _temp5(e_0) {
  return e_0.stopPropagation();
}
function _temp4(a_1) {
  return !!a_1;
}
function _temp3(a_0) {
  return !a_0.isLinked;
}
function _temp2(lastPage_0) {
  return lastPage_0?.nextDate ?? undefined;
}
function _temp(lastPage) {
  return lastPage?.nextDate ?? undefined;
}
function PickerTileImage(t0) {
  const $ = _c(12);
  const {
    thumbnailSignedUrl,
    signedUrl,
    alt,
    annotations
  } = t0;
  const [aspectRatio, setAspectRatio] = useState();
  let t1;
  if ($[0] !== alt || $[1] !== signedUrl || $[2] !== thumbnailSignedUrl) {
    t1 = <AttachmentPreviewImage thumbnailSignedUrl={thumbnailSignedUrl} signedUrl={signedUrl} alt={alt} iconClassName="h-8 w-8" onAspectRatio={setAspectRatio} />;
    $[0] = alt;
    $[1] = signedUrl;
    $[2] = thumbnailSignedUrl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== annotations || $[5] !== aspectRatio || $[6] !== signedUrl || $[7] !== thumbnailSignedUrl) {
    t2 = annotations.length > 0 && aspectRatio !== undefined && <AnnotationOverlay annotations={annotations} imageUrl={thumbnailSignedUrl || signedUrl || undefined} aspectRatio={aspectRatio} />;
    $[4] = annotations;
    $[5] = aspectRatio;
    $[6] = signedUrl;
    $[7] = thumbnailSignedUrl;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <div className="relative aspect-square overflow-hidden rounded-lg bg-muted">{t1}{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}